/**
 * @file: useRoomOrder.ts
 *
 * Resolves an order-internal room's order via the existing order search (by
 * orderNo). Shared by the room-detail order card and the header-right "open
 * order" link, so the order is fetched once per screen.
 */

import { ReceptionOrderStatus } from '@euler/model/enum';
import { SearchOrderResultItem } from '@euler/model/order/SearchOrderResultItem';
import { useServiceFactory } from '@euler/services/factory';
import { useEffect, useState } from 'react';

/** Where the room's header "open order" link goes. A finished order opens its
 * service record; anything else opens the live task detail. */
export type OrderLinkTarget =
  | { kind: 'task'; taskNo: string; label: string }
  | { kind: 'record'; id: string; label: string };

export function orderLinkTarget(
  order: SearchOrderResultItem | null,
): OrderLinkTarget | null {
  if (!order) return null;
  if (order.status === ReceptionOrderStatus.Finished && order.serviceRecordId) {
    return { kind: 'record', id: order.serviceRecordId, label: '服务记录' };
  }
  if (order.taskNo) {
    return { kind: 'task', taskNo: order.taskNo, label: '订单详情' };
  }
  return null;
}

export function useRoomOrder(orderNo: string | null | undefined): {
  order: SearchOrderResultItem | null;
  loading: boolean;
} {
  const { orderService } = useServiceFactory();
  const [order, setOrder] = useState<SearchOrderResultItem | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(orderNo));

  useEffect(() => {
    if (!orderNo) {
      setOrder(null);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    setOrder(null);
    void orderService
      .searchOrders({ orderNo }, undefined, 1)
      .then(res => {
        if (alive) setOrder(res.data[0] ?? null);
      })
      .catch(e => {
        console.error('load order for chat room failed', e);
        if (alive) setOrder(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [orderService, orderNo]);

  return { order, loading };
}
